import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import { about, stats, offices } from "../mock/mock";

const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("");

const About = () => {
  const navigate = useNavigate();
  useScrollReveal([]);
  const accents = ["var(--accent)", "var(--accent-2)", "var(--accent-3)", "var(--accent-4)"];

  return (
    <div className="bg-[#F4F1EA]">
      <Header forceSolid />

      {/* Hero */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] pt-36 pb-20 relative overflow-hidden">
        <svg className="absolute -right-24 top-6 w-[560px] opacity-[0.06] spin-slow hidden md:block" viewBox="0 0 52 30" fill="none">
          <path d="M9 15C9 9 15 9 19 15C23 21 29 21 33 15C37 9 43 9 43 15C43 21 37 21 33 15C29 9 23 9 19 15C15 21 9 21 9 15Z" stroke="#F4F1EA" strokeWidth="1.4" fill="none" />
        </svg>
        <div className="container-x relative">
          <p className="eyebrow text-[#F4F1EA]/50 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#F4F1EA]/50" /> About Infinity Solutions
          </p>
          <h1 className="mt-6 display font-extrabold uppercase leading-[0.92] tracking-tight text-[clamp(2.2rem,4.4vw,4rem)] max-w-4xl">
            One in a <span className="hl-invert">Billion</span>. Engineered to Lead.
          </h1>
          <p className="mt-7 text-[#F4F1EA]/70 text-lg max-w-2xl leading-relaxed">{about.intro}</p>
        </div>
      </section>

      {/* Story */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x grid lg:grid-cols-2 gap-14">
          <div>
            <p className="eyebrow text-neutral-500">/ Our Story</p>
            <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95]">
              Built on <span className="hl">Great Engineering</span>
            </h2>
          </div>
          <div className="space-y-5">
            {about.story.map((p, i) => (
              <p key={i} className="text-neutral-700 leading-relaxed text-lg">{p}</p>
            ))}
            <div className="border-l-4 border-[#0A0A0A] pl-5 mt-6">
              <p className="mono text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Our Mission</p>
              <p className="display text-xl font-bold">{about.mission}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-2 border-[#0A0A0A]">
          {stats.map((s, i) => (
            <div key={s.label} className={`p-7 border-[#0A0A0A] ${i % 6 !== 5 ? "lg:border-r-2" : ""} border-b-2 md:border-b-0 ${i % 2 === 0 ? "border-r-2 md:border-r-0" : ""} ${i < 3 ? "md:border-b-2 lg:border-b-0" : ""} md:border-r-2`}>
              <p className="display text-4xl md:text-5xl font-extrabold stroke-text">{s.value}{s.suffix}</p>
              <p className="mono text-[11px] uppercase tracking-widest text-neutral-500 mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ What We Value</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            Our <span className="hl">Principles</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {about.values.map((v, i) => {
              const Icon = Icons[v.icon] || Icons.Sparkles;
              return (
                <div key={v.title} className="card-brutal group p-7" style={{ "--ic": accents[i % accents.length] }}>
                  <div className="svc-icon w-12 h-12 border-2 border-[#0A0A0A] flex items-center justify-center mb-5">
                    <Icon size={22} className="text-[#0A0A0A] group-hover:text-[#F4F1EA] transition-colors" />
                  </div>
                  <h3 className="display text-lg font-bold uppercase mb-2">{v.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section-pad bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Leadership</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            The People <span className="hl">Behind It</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {about.leaders.map((l, i) => (
              <div key={l.name} className="card-brutal p-7 flex flex-col">
                <div className="w-16 h-16 border-2 border-[#0A0A0A] flex items-center justify-center display font-extrabold text-xl mb-5" style={{ background: accents[i % accents.length], color: "#F4F1EA" }}>
                  {initials(l.name)}
                </div>
                <h3 className="display text-lg font-bold uppercase">{l.name}</h3>
                <p className="mono text-[11px] uppercase tracking-widest text-neutral-500 mt-1">{l.role}</p>
                <p className="text-sm text-neutral-600 leading-relaxed mt-4">{l.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Our Journey</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            Milestones
          </h2>
          <div className="grid md:grid-cols-4 border-2 border-[#0A0A0A] bg-white">
            {about.milestones.map((m, i) => (
              <div key={m.year} className={`p-7 border-[#0A0A0A] ${i !== 3 ? "md:border-r-2" : ""} border-b-2 md:border-b-0`}>
                <p className="display text-3xl font-extrabold"><span className="hl">{m.year}</span></p>
                <p className="text-sm text-neutral-600 leading-relaxed mt-4">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="section-pad bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Where We Are</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            Global <span className="hl">Offices</span>
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {offices.map((o) => (
              <div key={o.country} className="border-2 border-[#0A0A0A] p-6">
                <p className="display font-bold uppercase mb-3">{o.country}</p>
                <p className="text-sm text-neutral-600 leading-relaxed">{o.address}</p>
                <p className="mono text-sm font-bold mt-3">{o.phone}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] py-20">
        <div className="container-x text-center">
          <h2 className="display text-3xl md:text-5xl font-extrabold uppercase leading-[0.95]">
            Let's Build Something <span className="hl-invert">Great</span>
          </h2>
          <button onClick={() => navigate("/#contact")} className="btn-paper mt-8 mx-auto group">
            Talk to Our Experts <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
